import { Injectable } from '@angular/core';
import { AppService } from 'src/app/app.service';
import { OrderDetailsGeneralObject } from 'src/app/models/orders/order-details-general/order-details-general-object.model';
import { OrderTypeObject } from 'src/app/models/orders/order-type-object.model';
import { CrmOrderObject } from 'src/app/models/orders/crm-order/crm-order-object.model';
import { TreeCardObject } from 'src/app/models/orders/tree-card/tree-card-object.model';
import { ProductOrderObject } from 'src/app/models/orders/product-order/product-order-object.model';
import { CfsOrderObject } from 'src/app/models/orders/cfs-order/cfs-order-object.model';
import { RfsOrderObject } from 'src/app/models/orders/rfs-order/rfs-order-object.model';
import { ItemObject } from 'src/app/models/orders/item/item-object.model';
import { isUndefined } from 'util';
import { OrderDetailsGeneralCancelRequest } from 'src/app/models/orders/order-details-general/order-details-general-cancel-request.model';
import { MessageObject } from 'src/app/models/message/message-object.model';
import { OrderStatusObject } from 'src/app/models/orders/order-status-object.model';
import { StringUtils } from 'src/app/services/utils/string.utils';
import { CharacteristicsConverter } from '../../characteristics/characteristics.converter';
import { OrderDetailsServicesConverter } from './order-details-services.converter';
import { OrderDetailsProductConverter } from './order-details-product.converter';
import { OrderDetailsProcessConverter } from './order-details-process.converter';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { OrderSummary } from '../orders.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  appData: any;

  constructor(
    private characteristicsConverter: CharacteristicsConverter,
    private serviceConverter: OrderDetailsServicesConverter,
    private productConverter: OrderDetailsProductConverter,
    private processConverter: OrderDetailsProcessConverter,
    private httpClient: HttpClient,
    appService: AppService,
  ) {
    this.appData = appService.settings;
  }

  getOrder(item: ItemObject): Observable<OrderDetailsGeneralObject> {
    const orderTypeEndpoint = this.getOrderTypeRequest(item.type);
    return this.httpClient.get<any>(`${this.appData.webApi}${orderTypeEndpoint}/${item.orderId}/summary`)
      .pipe(
        map(orderData => {
          let orderDetailsObject: OrderDetailsGeneralObject;
          if (!orderData) {
            orderDetailsObject = OrderDetailsGeneralObject.create();
          } else {
            orderDetailsObject = new OrderDetailsGeneralObject(StringUtils.default(orderData.id),
              OrderStatusObject.createWithStatus(orderData.state), StringUtils.default(orderData.domain),
              OrderTypeObject.createWithType(orderData.type), StringUtils.default(orderData.id),
              StringUtils.default(orderData.status), StringUtils.default(orderData.externalId), orderData.priority,
              orderData.urgent, orderData.requestStartDate, orderData.expectedStartDate,
              this.characteristicsConverter.convert(orderData.listsCharacteristic), orderData.cancelationMotive,
              this.productConverter.convert(orderData), this.serviceConverter.convert(orderData),
              orderData.isCancellable, this.processConverter.convert(orderData));
          }
          return orderDetailsObject;
        }),
        catchError(error => {
          console.error(error);
          return of(OrderDetailsGeneralObject.create());
        })
      );
  }

  private getOrderTypeRequest(type: OrderTypeObject) {

    if (type.equals(OrderTypeObject.CRM) || type.equals(OrderTypeObject.PRODUCT)) return '/products';
    if (type.equals(OrderTypeObject.CFS) || type.equals(OrderTypeObject.RFS)) return '/services';

    throw Error(`The type ${type.name} is not valid.`);
  }

  getTree(crmId: string): Observable<CrmOrderObject> {
    return this.httpClient.get(`${this.appData.webApi}/orders/crm/${crmId}/tree`)
      .pipe(
        map(treeJSON => this.computeTree(treeJSON, crmId) as CrmOrderObject),
      );
  }

  private computeTree(treeNodeJSON: any, crmId: string): TreeCardObject {
    const treeNodeObject: TreeCardObject = this.createNode(treeNodeJSON, crmId);
    if (!isUndefined(treeNodeObject) && !isUndefined(treeNodeJSON.children)) {
      treeNodeJSON.children.forEach(async child => {
        const childNodeObject: TreeCardObject = this.computeTree(child, crmId);
        treeNodeObject.addChildren(childNodeObject);
      });
    }
    return treeNodeObject;
  }

  private createNode(treeNodeJSON: any, crmId: string): TreeCardObject {
    const type: OrderTypeObject = OrderTypeObject.createWithType(treeNodeJSON.type);
    switch (type) {
      case OrderTypeObject.CRM: return new CrmOrderObject(treeNodeJSON.name, treeNodeJSON.status,
        treeNodeJSON.orderId, treeNodeJSON.crmId);
      case OrderTypeObject.PRODUCT: return new ProductOrderObject(treeNodeJSON.name,
        treeNodeJSON.completed, crmId, treeNodeJSON.operation, treeNodeJSON.status,
        treeNodeJSON.dependencies, treeNodeJSON.orderId);
      case OrderTypeObject.CFS: return new CfsOrderObject(treeNodeJSON.name,
        treeNodeJSON.completed, crmId, treeNodeJSON.operation, treeNodeJSON.status,
        treeNodeJSON.dependencies, treeNodeJSON.orderId);
      case OrderTypeObject.RFS: return new RfsOrderObject(treeNodeJSON.name,
        treeNodeJSON.completed, crmId, treeNodeJSON.operation, treeNodeJSON.status,
        treeNodeJSON.dependencies, treeNodeJSON.orderId);
    }
    return new TreeCardObject(treeNodeJSON.name, '', '', treeNodeJSON.completed, treeNodeJSON.orderId, treeNodeJSON.operation,
      treeNodeJSON.status, treeNodeJSON.dependencies, 0, treeNodeJSON.orderId, OrderTypeObject.PRODUCT);
  }

  cancelOrder(orderId: string, comment: MessageObject): Observable<any> {
    const body: OrderDetailsGeneralCancelRequest = new OrderDetailsGeneralCancelRequest(comment.message);
    return this.httpClient.post(`${this.appData.webApi}/orders/${orderId}/cancel?scope=productorder`, body);
  }

  existsExternalId(externalId: string): Observable<string> {
    return this.httpClient.get<HttpResponse<void>>(`${this.appData.webApi}/order-repository/orders/customerOrder/${externalId}/customer-portal`, { observe: 'response' })
      .pipe(
        switchMap(() => throwError({ status: 200 })),
        catchError(error => {
          if (error.status === 404) {
            return of(externalId);
          }
          return throwError({ status: 200 });
        }),
      );
  }

  isTerminable(productRef: string): Observable<boolean> {
    const params = new HttpParams().append('productRef', productRef);

    return this.httpClient.get<{ isTerminable: boolean }>(`${this.appData.webApi}/order-repository/productOrder/checkIsTerminable`, { params })
      .pipe(
        map(response => response.isTerminable),
        catchError(() => of(false)),
      );
  }

  isCancellable(productRef: string): Observable<{ isCancellable: boolean; productOrderId: string; }> {
    const params = new HttpParams().append('productRef', productRef);

    return this.httpClient.get<{ isCancellable: boolean; productOrderId: string; }>(`${this.appData.webApi}/order-repository/productOrder/checkIsCancellable`, { params });
  }

  isModifiable(productRef: string): Observable<boolean> {
    const params = new HttpParams().append('productRef', productRef);

    return this.httpClient.get<{ isModificable: boolean }>(`${this.appData.webApi}/order-repository/productOrder/checkIsModificable`, { params })
      .pipe(
        map(response => response.isModificable),
        catchError(() => of(false)),
      );
  }

  cancel(productOrderId: string, externalId: string, cancellationMotive: string): Observable<any> {
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
    const now = moment();

    return this.httpClient.put(`${this.appData.webApi}/order-repository/productOrder/productOrders/${productOrderId}/cancel`, {
      externalId,
      cancellationMotive,
      cancellationDate: now.format(dateFormat)
    });
  }

  decomposeProductOrder(productOrderId: string, orderSummary: OrderSummary): Observable<any> {
    return this.httpClient.put<any>(`${this.appData.webApi}/order-repository/productOrder/${productOrderId}/decompose`, orderSummary);
  }

}
